(() => {
  const walk = (options, active = true) => (options || []).flatMap(option => {
    const visible = active && option.visible !== false;
    return [{ option, active: visible }, ...walk(option.options, visible)];
  });
  const activeRate = instance => {
    const entries = walk(instance.config?.options);
    const rates = [];
    for (const entry of entries) {
      const match = /^(?:_(.+)_)?平米単価（円）$/.exec(entry.option.name || '');
      if (!match || !entry.active) continue;
      const prefix = match[1] ? `_${match[1]}_` : '_';
      const controls = entries.filter(({ option }) => ['width', 'length'].some(axis =>
        ['m', 'cm'].some(unit => option.name === `${prefix}${axis}_${unit}`)));
      if (controls.length && !controls.some(control => control.active)) continue;
      const raw = entry.option.value;
      if (!['number', 'string'].includes(typeof raw) || !/^\d+$/.test(String(raw))) continue;
      const rate = Number(raw);
      if (Number.isSafeInteger(rate) && rate > 0) rates.push(rate);
    }
    const unique = [...new Set(rates)];
    return unique.length === 1 ? unique[0] : null;
  };
  let nextId = 0;
  class LpoPriceInfo extends HTMLElement {
    connectedCallback() {
      this.root = this.closest('.product-details');
      this.trigger = this.querySelector('button');
      this.panel = this.querySelector('[popover]');
      if (!this.root || !this.trigger || !this.panel) return;
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.instances = new Set();
      this.panel.id = `LpoPriceInfo-${++nextId}`;
      this.trigger.setAttribute('popovertarget', this.panel.id);
      this.trigger.setAttribute('aria-controls', this.panel.id);
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.addEventListener('click', () => clearTimeout(this.timer), { signal });
      this.panel.addEventListener('toggle', () => {
        this.trigger.setAttribute('aria-expanded', String(this.panel.matches(':popover-open')));
      }, { signal });
      this.panel.addEventListener('beforetoggle', event => {
        if (event.newState === 'open') requestAnimationFrame(() => this.position());
      }, { signal });
      this.addEventListener('pointerenter', event => {
        if (event.pointerType !== 'mouse' || !matchMedia('(hover: hover) and (pointer: fine)').matches || this.hidden) return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => { if (this.isConnected && !this.hidden) this.panel.showPopover(); }, 150);
      }, { signal });
      this.addEventListener('pointerleave', event => {
        if (event.pointerType !== 'mouse') return;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.close(), 200);
      }, { signal });
      this.panel.addEventListener('pointerenter', () => clearTimeout(this.timer), { signal });
      this.panel.addEventListener('pointerleave', event => {
        if (event.pointerType === 'mouse') this.timer = setTimeout(() => this.close(), 200);
      }, { signal });
      for (const type of ['options:rendered', 'option:changed', 'options:price']) {
        window.addEventListener(type, event => {
          const instance = event.detail;
          if (instance?.config?.options && instance.element) this.instances.add(instance);
          this.schedule();
        }, { signal });
      }
      document.addEventListener('pointerdown', event => {
        if (!this.contains(event.target)) this.close();
      }, { signal, capture: true });
      document.addEventListener('keydown', event => {
        if (event.key === 'Escape') this.close();
      }, { signal });
      window.addEventListener('resize', () => this.close(), { signal });
      window.addEventListener('scroll', () => this.close(), { signal, capture: true });
      this.observer = new MutationObserver(() => this.schedule());
      this.observer.observe(this.root, { childList: true, subtree: true });
      this.update();
    }
    schedule() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => { this.frame = null; this.update(); });
    }
    update() {
      for (const instance of window.Cloudlift?.options?.App?.products || []) this.instances.add(instance);
      const rates = [];
      for (const instance of this.instances) {
        if (!instance.element?.isConnected || instance._wrapper?.isConnected === false) {
          this.instances.delete(instance);
          continue;
        }
        if (!this.root.contains(instance.element)) continue;
        const rate = activeRate(instance);
        if (rate !== null) rates.push(rate);
      }
      const unique = [...new Set(rates)];
      const rate = unique.length === 1 ? unique[0] : null;
      const text = rate === null ? '' : this.dataset.copy.replace('__RATE__', rate.toLocaleString('ja-JP'));
      if (this.panel.textContent !== text) {
        this.close();
        this.panel.textContent = text;
      }
      this.hidden = rate === null;
    }
    position() {
      const anchor = this.trigger.getBoundingClientRect();
      const box = this.panel.getBoundingClientRect();
      const left = Math.max(16, Math.min(anchor.left + anchor.width / 2 - box.width / 2, innerWidth - box.width - 16));
      const above = anchor.top - box.height - 8;
      const top = above >= 16 ? above : Math.min(anchor.bottom + 8, innerHeight - box.height - 16);
      this.panel.style.left = `${left}px`;
      this.panel.style.top = `${Math.max(16, top)}px`;
    }
    close() {
      clearTimeout(this.timer);
      if (this.panel?.matches(':popover-open')) this.panel.hidePopover();
    }
    disconnectedCallback() {
      this.close();
      this.abort?.abort();
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }
  if (!customElements.get('jp-lpo-price-info')) customElements.define('jp-lpo-price-info', LpoPriceInfo);
})();
